import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet-async';
import { AppConfig } from '../../../core/constants';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import HttpsIcon from '@mui/icons-material/Https';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { Alert, Chip, Skeleton, Stack } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import CustomButton from '../../../components/common/CustomButton';
import { useAuth } from '../../../components/auth';
import {
  useGetPricing,
  useBillingCheckOutSession,
} from '../../../hooks/billingHooks';
import { faqs } from '../../../core/constants/constants';
import { CreateCheckOutRequest } from '../../../types';
import { ApiResponse } from '../../../types/api';
import useSubscription from '../../../hooks/useSubscription';
import { pricingPlans, month, year } from '../../../core/constants/constants';

export default function Pricing() {
  const { palette, typography } = useTheme();
  const navigate = useNavigate();
  const [billing, setBilling] = useState<typeof month | typeof year>(year);
  const [currency, setCurrency] = useState<string>('gbp');

  const [prices, setPrices] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);

  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { isPremium } = useSubscription();

  const { data, isLoading, isError } = useGetPricing();

  const mutation = useBillingCheckOutSession();

  useEffect(() => {
    if (data) {
      const pricesData = data as any;
      setPrices(pricesData);
      const uniqueCurrencies: string[] = Array.from(
        new Set(pricesData.map((price: any) => price.currency)),
      );
      setCurrencies(uniqueCurrencies);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      setError('Failed to load pricing information. Please try again later.');
    }
  }, [isError]);

  const getPriceFor = (
    prices: any[],
    currency: string,
    interval: string,
    planType?: string,
  ) => {
    if (planType === 'one_time') {
      return prices.find((p) => p.currency === currency && p.type === planType);
    }
    return prices.find(
      (p) =>
        p.currency === currency &&
        p.interval === interval &&
        p.type === planType,
    );
  };

  const subscription = getPriceFor(prices, currency, billing, 'recurring');
  const oneOffPayment = getPriceFor(prices, currency, billing, 'one_time');

  const currencySymbols: { [key: string]: string } = {
    usd: '$',
    gbp: '£',
    eur: '€',
  };

  const handleChange = (event: SelectChangeEvent) => {
    setCurrency(event.target.value as string);
  };

  const matchPayment = (subType: string) => {
    switch (subType) {
      case 'subscription':
        return handleCheckOut(subscription?.price_id, subType);
      case 'payment':
        return handleCheckOut(oneOffPayment?.price_id, subType);
      default:
        break;
    }
  };

  const handleCheckOut = async (priceId: string, mode: string) => {
    if (!currentUser) {
      navigate('/auth/login');
      return;
    }

    const payload: CreateCheckOutRequest = {
      price_id: priceId,
      mode: mode,
    };
    mutation.mutate(payload, {
      onSuccess: (
        response: ApiResponse<{ sessionId: string; url: string }>,
      ) => {
        enqueueSnackbar('Checkout session created successfully', {
          variant: 'success',
        });
        let url: string | undefined = undefined;
        if (response && typeof response === 'object') {
          if ('url' in response && typeof response.url === 'string') {
            url = response.url;
          } else if (
            response.data &&
            typeof response.data === 'object' &&
            'url' in response.data &&
            typeof response.data.url === 'string'
          ) {
            url = response.data.url;
          }
        }
        if (url) {
          window.location.href = url;
        } else {
          enqueueSnackbar('Checkout session URL not found.', {
            variant: 'error',
          });
        }
      },
      onError: (error) => {
        console.error('Error creating checkout session:', error);
        enqueueSnackbar(`Error creating checkout session: ${error.message}`, {
          variant: 'error',
        });
      },
    });
  };

  return (
    <Box mt="64px" sx={{ height: '100%' }}>
      <Helmet>
        <title>Pricing | {AppConfig.appName}</title>
        <meta name="description" content="View pricing plans for LegacTies" />
        <meta property="og:title" content={`${AppConfig.appName} - Pricing`} />
        <meta
          property="og:description"
          content="View pricing plans for LegacTies"
        />
      </Helmet>
      <Container
        maxWidth="xl"
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          my: 14,
          gap: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: typography.h3.fontSize,
                md: typography.h1.fontSize,
              },
              fontWeight: {
                xs: typography.h1.fontWeight,
              },
              color: palette.text.primary,
              textAlign: 'center',
              fontFamily:
                '"Playfair Display", "Georgia", "Times New Roman", serif !important',
            }}
            gutterBottom
          >
            Your history has survived a lot.
            <br />
            It deserves a safe home.
          </Typography>
          <Typography
            sx={{
              color: palette.text.primary,
              fontSize: {
                xs: typography.body2.fontSize,
                md: typography.h5.fontSize,
              },
              lineHeight: '1.8',
              textAlign: 'center',
            }}
          >
            Start free. No credit card needed. Upgrade only when your family's
            story outgrows the plan.
            <br />
            Because the stories your elders carried shouldn't disappear when the
            last person who remembers them does.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            alignItems="center"
            sx={{
              p: 1,
              px: 3,
              borderRadius: 8,
              border: `1px solid ${palette.divider}`,
              bgcolor: 'background.paper',
              boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, color: 'text.disabled' }}
              >
                Currency
              </Typography>
              <Stack direction="row" spacing={1}>
                {currencies.map((curr) => (
                  <Chip
                    key={curr}
                    label={curr.toUpperCase()}
                    onClick={() =>
                      handleChange({ target: { value: curr } } as any)
                    }
                    variant={currency === curr ? 'filled' : 'outlined'}
                    color={currency === curr ? 'primary' : 'default'}
                    sx={{
                      borderRadius: '8px',
                      fontWeight: 600,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor:
                          currency === curr ? 'primary.main' : 'action.hover',
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', sm: 'block' },
                width: '1px',
                height: '24px',
                bgcolor: 'divider',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                p: 0.5,
                bgcolor: 'action.hover',
                borderRadius: 4,
              }}
            >
              <Button
                onClick={() => setBilling(month)}
                sx={{
                  px: 3,
                  borderRadius: 3,
                  bgcolor:
                    billing === month ? 'background.paper' : 'transparent',
                  color: billing === month ? 'primary.main' : 'text.secondary',
                  boxShadow: billing === month ? 'inherit' : 'none',
                  '&:hover': {
                    bgcolor:
                      billing === month
                        ? 'background.paper'
                        : 'action.selected',
                  },
                }}
              >
                Monthly
              </Button>
              <Button
                onClick={() => setBilling(year)}
                sx={{
                  px: 3,
                  borderRadius: 3,
                  bgcolor:
                    billing === year ? 'background.paper' : 'transparent',
                  color: billing === year ? 'primary.main' : 'text.secondary',
                  boxShadow: billing === year ? 'inherit' : 'none',
                  '&:hover': {
                    bgcolor:
                      billing === year ? 'background.paper' : 'action.selected',
                  },
                }}
              >
                Annual
                <Chip
                  label="Save 15%"
                  size="small"
                  color="success"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 800,
                  }}
                />
              </Button>
            </Box>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {isPremium && (
            <Alert severity="info" variant="filled">
              You have an active subscription
            </Alert>
          )}
        </Box>
        <Grid
          container
          spacing={3}
          justifyContent="center"
          sx={{
            animation: 'slideUp 0.8s ease-out forwards',
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(40px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {isLoading
            ? Array.from(new Array(3)).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card sx={{ p: 3, borderRadius: 5 }}>
                    <Skeleton variant="text" height={40} />
                    <Skeleton variant="text" height={60} />
                    <Skeleton
                      variant="rectangular"
                      height={120}
                      sx={{ my: 2 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Card>
                </Grid>
              ))
            : pricingPlans.map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.title}>
                  <Card
                    sx={{
                      px: 2,
                      border: plan.popular
                        ? `2px solid ${palette.primary.light}`
                        : '1px solid',
                      borderColor: plan.popular
                        ? palette.primary.light
                        : palette.divider,
                      boxShadow: plan.popular ? 6 : 1,
                      position: 'relative',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 5,
                      '&:hover': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s ease-in-out',
                      },
                    }}
                  >
                    {plan.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          bgcolor: palette.info.main,
                          color: palette.primary.contrastText,
                          px: 2,
                          py: 0.5,
                          borderRadius: 4,
                          fontWeight: 600,
                          fontSize: 12,
                          letterSpacing: 1,
                        }}
                      >
                        Most Popular
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1, textAlign: 'left', pt: 5 }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        {plan.title}
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: 40,
                          fontWeight: 700,
                          color: palette.text.primary,
                          mb: 2,
                        }}
                      >
                        {plan.id === 'free' && 'Free'}
                        {plan.id === 'premium' && subscription && (
                          <>
                            {currencySymbols[subscription.currency] ||
                              subscription.currency.toUpperCase()}
                            {subscription.unit_amount}
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 18,
                                color: palette.text.secondary,
                              }}
                            >
                              {billing === year ? '/yr' : '/mo'}
                            </Typography>
                          </>
                        )}
                        {plan.id === 'assisted' && oneOffPayment && (
                          <>
                            {currencySymbols[oneOffPayment.currency] ||
                              oneOffPayment.currency.toUpperCase()}
                            {oneOffPayment.unit_amount}
                          </>
                        )}
                        {plan.id === 'community' && (
                          <>From {currencySymbols[currency]}499</>
                        )}
                        {error && plan.id !== 'free' && 'N/A'}
                      </Typography>
                      <Box component="p" pb={2} color="text.secondary">
                        {plan.subTitle}
                      </Box>
                      <Box
                        component="ul"
                        sx={{ listStyle: 'none', p: 0, m: 0, mb: 3 }}
                      >
                        {plan.description.map((line) => (
                          <Box
                            component="li"
                            key={line}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              mb: 1,
                              color: palette.text.secondary,
                            }}
                          >
                            <CheckCircleOutlineIcon
                              color="info"
                              sx={{ mr: 1, fontSize: 20 }}
                            />
                            <Typography variant="body1">{line}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>

                    {plan.notes && (
                      <Box sx={{ my: 2, textAlign: 'center' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: palette.text.secondary,
                          }}
                        >
                          {plan.notes}
                        </Typography>
                      </Box>
                    )}
                    {plan.scheduleMeeting && plan.id === 'assisted' && (
                      <CardActions sx={{ justifyContent: 'center' }}>
                        <CustomButton
                          label="Book My Griot Setup"
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={() =>
                            (window.location.href = AppConfig.calendlyLink)
                          }
                          sx={{
                            borderRadius: 2,
                            '&:hover': {
                              color: palette.primary.contrastText,
                              border: 'none',
                            },
                            ...(plan.popular ? { boxShadow: 2 } : {}),
                          }}
                          fullWidth
                        />
                      </CardActions>
                    )}
                    {['free', 'assisted', 'premium'].includes(plan.id) ? (
                      <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <CustomButton
                          label={plan.buttonText}
                          variant={
                            plan.buttonVariant as 'contained' | 'outlined'
                          }
                          color="primary"
                          size="large"
                          onClick={
                            plan.id === 'free'
                              ? () => navigate('/app')
                              : () =>
                                  matchPayment(
                                    plan.id === 'assisted'
                                      ? 'payment'
                                      : 'subscription',
                                  )
                          }
                          sx={{
                            borderRadius: 2,
                            '&:hover': {
                              color: palette.primary.contrastText,
                              border: 'none',
                            },
                            ...(plan.popular ? { boxShadow: 2 } : {}),
                          }}
                          fullWidth
                        />
                      </CardActions>
                    ) : (
                      <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <CustomButton
                          label={plan.buttonText}
                          variant={
                            plan.buttonVariant as 'contained' | 'outlined'
                          }
                          color="primary"
                          size="large"
                          onClick={() =>
                            (window.location.href = `mailto:${AppConfig.appContactEmail}`)
                          }
                          sx={{
                            borderRadius: 2,
                            '&:hover': {
                              color: palette.primary.contrastText,
                              border: 'none',
                            },
                          }}
                          fullWidth
                        />
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
        </Grid>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 4,
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1,
            }}
          >
            <HttpsIcon sx={{ color: palette.info.main, fontSize: 24 }} />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: palette.info.main,
              fontWeight: 600,
              letterSpacing: 0.5,
              fontSize: typography.h4.fontSize,
            }}
          >
            All transactions are secure.
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 8,
            py: 4,
            maxWidth: 900,
            mx: 'auto',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: palette.text.primary,
              mb: 3,
              textAlign: 'center',
              fontWeight: '700',
              fontFamily:
                '"Playfair Display", "Georgia", "Times New Roman", serif !important',
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Accordion
              sx={{
                mb: 1,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:before': { display: 'none' },
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="secondary" />}
                aria-controls="panel1d-content"
                id="panel1d-header"
                sx={{
                  bgcolor: 'background.paper',
                  px: 2,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  How do I contact customer support if I have a question or
                  issue?
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 2, py: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{ color: palette.text.secondary }}
                >
                  You can reach our customer support team by emailing{' '}
                  <Link
                    href={`mailto:${AppConfig.appContactEmail}`}
                    underline="hover"
                    color="primary.main"
                  >
                    {AppConfig.appContactEmail}
                  </Link>
                  . We&apos;re here to assist you promptly.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {faqs.map((faq, idx) => (
              <Accordion
                key={faq.question}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:before': { display: 'none' },
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="secondary" />}
                  aria-controls={`faq-content-${idx}`}
                  id={`faq-header-${idx}`}
                  sx={{
                    bgcolor: 'background.paper',
                    px: 2,
                    '& .MuiAccordionSummary-content': {
                      my: 1,
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, py: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: palette.text.secondary }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
          <Card
            sx={{
              p: 3,
              borderRadius: 5,
              textAlign: 'center',
              maxWidth: 600,
              mx: 'auto',
              background: `linear-gradient(135deg, ${palette.primary.main}, ${palette.secondary.main})`,
              color: palette.primary.contrastText,
              boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            }}
          >
            <CardContent>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                The Gift of Heritage
              </Typography>

              <Typography sx={{ my: 2 }}>
                Looking for a meaningful gift for a parent or grandparent?
                Purchase The Roots (Builder Plan) plan as a gift voucher.
                <br />
                Give them the joy of seeing their life story organised,
                honoured, and preserved for their grandchildren.
              </Typography>

              <CustomButton
                label="Buy as a Gift"
                color="secondary"
                onClick={() => matchPayment('subscription')}
                size="medium"
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: '0.9rem',
                    md: typography.body1.fontSize,
                  },
                  color: palette.primary.main,
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  '&:hover': {
                    color: 'white',
                  },
                }}
              />
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
